import createAuthorizationClient from "@izettle/authorization-sdk-js"
import type AuthorizationClient from "@izettle/authorization-sdk-js/dist/typings/authorization-client"
import { Scope } from "@izettle/constants"

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    authorizationClient?: AuthorizationClient | null
    redirectTo?: string
  }
}

const PRODUCTION_CLIENT_ID = "cb01c45a-c014-45fb-8ef2-c7e298c53cd2"
const STAGING_CLIENT_ID = "d46c8543-6614-4590-ba57-cca5aced8028"
const DEFAULT_SCOPES = [
  Scope.ALL_INTERNAL,
  Scope.READ_FINANCE,
  Scope.READ_PURCHASE,
  Scope.READ_PRODUCT,
  Scope.WRITE_PRODUCT,
  Scope.READ_ONLINEPAYMENT,
  Scope.READ_USERINFO,
  Scope.WRITE_USERINFO,
]

const CALLBACK_PATHNAME = "/dashboard"
const getRedirectionURI = () => `${window.location.origin}${CALLBACK_PATHNAME}`

type BaseAppState = {
  originalPath?: string
}

type AppState = (BaseAppState & Record<string, any>) | null

type InitOptions = {
  loginServiceUrl: string
  authServiceUrl: string
}

type AuthModule = {
  client: null | Promise<AuthorizationClient> | AuthorizationClient
  init: (options: InitOptions) => Promise<AuthorizationClient>
  state?: AppState
}
const auth: Partial<AuthModule> = {
  client: null,
}
/**
 * Adds `originalUrl` to the `appState`.
 */
const withOriginalPath =
  (client: AuthorizationClient, fn: "loginWithRedirect" | "verifyWithRedirect") =>
  (_error: unknown, options: RedirectLoginOptions) =>
    client[fn]({
      ...options,
      appState: {
        ...(options.appState ?? {}),
        originalPath: `${window.location.pathname}${window.location.search}`,
      },
    })

const handleOneTimeToken = (
  params: URLSearchParams,
  loginUrl = "https://login.zettletest.com",
) => {
  const oneTimeToken = params.get("one_time_token")

  // We have to remove the OTT from the original URL so we don't get stuck in a redeeming loop
  params.delete("one_time_token")

  const newParams = params.toString()
  let originalUrl = window.location.origin + window.location.pathname

  if (newParams.length > 0) {
    originalUrl += `?${newParams}`
  }

  const redirection = `${loginUrl}/redeem_one_time_token/${oneTimeToken}?redirectTo=${encodeURIComponent(
    originalUrl,
  )}`

  window.location.assign(redirection)
}
const defaultPush = (path: string) => {
  window.redirectTo = path
}

const handleOAuthCallback = (client: AuthorizationClient) =>
  client.handleRedirectCallback().then(({ appState }: { appState?: AppState }) => {
    // For asserting in tests
    if (process.env.NODE_ENV === "test") {
      window.history.pushState({}, "Authenticated")
    }

    if (appState) {
      if (appState.originalPath) {
        defaultPush(appState.originalPath)
      }
      auth.state = appState
    }
  })

auth.init = async ({ authServiceUrl, loginServiceUrl }: InitOptions) => {
  // returns either proper client or a promise of a client
  // since the function is async
  // it will be Promise either way
  if (auth.client) return auth.client

  const clientPromise = createAuthorizationClient({
    domain: new URL(authServiceUrl).host,
    client_id:
      process.env.APP_ENV === "production" ? PRODUCTION_CLIENT_ID : STAGING_CLIENT_ID,
    redirect_uri: getRedirectionURI(),
    scope: DEFAULT_SCOPES.join(" "),
  })
  // Set the client immediatly (even though it's still a promise)
  auth.client = clientPromise

  // check for one_time_token to do the redirect before client redirect starts
  const params = new URLSearchParams(window.location.search)

  if (params.get("one_time_token") != null) {
    handleOneTimeToken(params, loginServiceUrl)
    return auth.client
  }

  const client = await clientPromise
  auth.client = client

  // Overwrite the error handlers for `getToken` with our own that adds
  // `originalPath` to the state, so we can return to the page we were on
  const originalGetToken = client.getToken.bind(client)
  client.getToken = (options, handlers) =>
    originalGetToken(options, {
      loginRequiredHandler:
        handlers?.loginRequiredHandler ?? withOriginalPath(client, "loginWithRedirect"),
      interactionRequiredHandler:
        handlers?.interactionRequiredHandler ??
        withOriginalPath(client, "verifyWithRedirect"),
    })

  const isOnCallbackPage = window.location.pathname === CALLBACK_PATHNAME
  // Checking `isOnCallbackPage` before processing `code` param assures we do not process
  // the `code` param unnecessarily, and only do so when we are actually on the OAuth
  // callback page.
  // It is important that the callback processing of Zettle's OAuth callback not interfere
  // with other URLs that might be making use of the `code` param, which is not limited
  // to OAuth only.
  // TODO: A beter solution would be to move the OAuth Callback to it's separate PATH -> /callback ?
  if (isOnCallbackPage && params.get("code") != null) {
    // We need to have a default value for `push` here in case there is no react-router-dom Context.
    void handleOAuthCallback(client).catch((err) => {
      // Handle login flows expiring due to inactivity (e.g. internet loss)
      if (err.message === "Invalid state") {
        // Re-do login flow - if they successfully logged in we'll just get a new token
        // Otherwise they'll get sent to the login pages again! :D
        void client?.loginWithRedirect()
      }
    })

    return client
  }

  // getToken checks if the user is logged in and starts the login flow if they're not
  await client.getToken()

  return client
}

export { auth }
export type { AuthorizationClient }
